import type { BriefDetails } from '@mybusinessmate/schema';
import { dayText } from './workbench-display';

const PLATFORM: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook' };
const FORMAT: Record<string, string> = { single_image: 'Single image', carousel: 'Carousel' };
const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

const platformsText = (platforms: string[]) => platforms.map((p) => PLATFORM[p] ?? p).join(' + ');
/** "Fri 24 Oct · 7:00 PM": a publish time is an instant, so it is read on the viewer's own clock. */
const whenText = (iso: string) => `${dayText(iso)} · ${time.format(new Date(iso))}`;
const firstLine = (text: string) => text.split('\n')[0]!.trim();

export interface BriefRow {
  label: string;
  value: string;
}

/**
 * The few lines of "What was asked · Brief v0" in the quick peek (Figma A05.7d): a key and a value each, and nothing
 * for what the brief left blank. The full brief is one click away on the task page.
 */
export function briefRows(details: BriefDetails): BriefRow[] {
  const rows: (BriefRow | null)[] = [];
  const row = (label: string, value: string | null | undefined) =>
    rows.push(value && value.trim() ? { label, value: value.trim() } : null);

  switch (details.type) {
    case 'post':
      row(
        'Format',
        [FORMAT[details.format ?? ''] ?? details.format, platformsText(details.platforms)].filter(Boolean).join(' · '),
      );
      row('Caption', details.caption && firstLine(details.caption));
      row('Audio', details.audioNote);
      row('Publish', details.scheduledUploadAt && whenText(details.scheduledUploadAt));
      break;
    case 'reel':
      row(
        'Format',
        ['Reel', details.durationSeconds ? `${details.durationSeconds} s` : null, platformsText(details.platforms)]
          .filter(Boolean)
          .join(' · '),
      );
      row('Hook', details.script && firstLine(details.script));
      row('Audio', details.audioNote);
      row('Publish', details.scheduledUploadAt && whenText(details.scheduledUploadAt));
      break;
    case 'website':
      row('Goal', details.goalAudience && firstLine(details.goalAudience));
      row('Style', details.style);
      row('Pages', details.pages.length > 0 ? details.pages.map((p) => p.title).join(', ') : null);
      row('Live by', details.liveBy && dayText(details.liveBy));
      break;
    case 'monthly_update':
      row('Page', details.pageUrl);
      row('Change', details.description && firstLine(details.description));
      row('Live by', details.liveBy && dayText(details.liveBy));
      break;
    case 'custom':
      row('Details', details.details && firstLine(details.details));
      break;
  }
  return rows.filter((r): r is BriefRow => r !== null);
}
